import React, { Component } from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { Button, Card, Checkbox, Dimmer, Icon, Label, Loader, Message, Modal, Popup } from "semantic-ui-react";
import PropTypes from "prop-types";
import moment from "moment";
import Immersive3dTourFloorPlanImportModalStore from "../stores/Immersive3dTourFloorPlanImportModalStore";
import ContentLoader from "~/components/element/ContentLoader";
import "../styles/FloorPlanImportModal.less";
import Immersive3dTourFloorPlanImportStatus from "~/constants/Immersive3dTour/Immersive3dTourFloorPlanImportStatus";
import FlashMessage from "~/components/FlashMessage";
import ImageWithLoading from "~/components/ImageWithLoading";
import Immersive3dTourStore from "~/stores/Immersive3dTour/Immersive3dTourStore";

@observer
class FloorPlanImportModal extends Component {
  timerForFloorPlanUpdateRefresh = null;

  constructor(props) {
    super(props);

    this.state = { hasRequestedFloorPlansUpdate: false };

    this.floorPlanUpdateAutoRefresh();
  }

  @computed
  get hasPendingFloorPlanUpdateRequest() {
    const { hasRequestedFloorPlansUpdate } = this.state;
    const { immersive3dTourStore } = this.props;
    const { immersive3dTour } = immersive3dTourStore;

    if (hasRequestedFloorPlansUpdate) {
      return true;
    }

    return (
      immersive3dTour.floorPlanUpdateRequestDts !== null &&
      immersive3dTour.floorPlanUpdateRequestDts > immersive3dTour.floorPlanLastUpdateDts &&
      moment() < immersive3dTour.floorPlanUpdateRequestTimeoutDts
    );
  }

  @computed
  get allowImportForAlreadyImportedFloorPlans() {
    const { immersive3dTourFloorPlanImportModalStore, immersive3dTourStore } = this.props;
    const { lastPhotoUploadedDate } = immersive3dTourFloorPlanImportModalStore;
    const { immersive3dTour } = immersive3dTourStore;

    // We don't have any uploaded photos
    if (lastPhotoUploadedDate === null) {
      return false;
    }

    // The floor plans have never been updated
    if (immersive3dTour.floorPlanLastUpdateDts === null) {
      return false;
    }

    // There is at least one photo upload before the last update
    // That makes the photo upload not up to date
    // Allow import
    return lastPhotoUploadedDate < immersive3dTour.floorPlanLastUpdateDts;
  }

  getTourJobFileDownloadUrl(tourJobFileId) {
    const { immersive3dTourFloorPlanImportModalStore } = this.props;
    const { tourJobFileStore } = immersive3dTourFloorPlanImportModalStore;

    return tourJobFileStore.getTourJobFileDownloadUrl(tourJobFileId);
  }

  floorPlanUpdateAutoRefresh(force = false) {
    const { immersive3dTourFloorPlanImportModalStore, immersive3dTourStore } = this.props;

    clearInterval(this.timerForFloorPlanUpdateRefresh);

    if (force || this.hasPendingFloorPlanUpdateRequest) {
      this.timerForFloorPlanUpdateRefresh = setInterval(() => {
        immersive3dTourStore.getOne().then(() => {
          const { immersive3dTour } = immersive3dTourStore;

          if (
            (immersive3dTour.floorPlanLastUpdateDts !== null &&
              immersive3dTour.floorPlanUpdateRequestDts <= immersive3dTour.floorPlanLastUpdateDts) ||
            moment() >= immersive3dTour.floorPlanUpdateRequestTimeoutDts
          ) {
            this.setState({ hasRequestedFloorPlansUpdate: false });
            clearInterval(this.timerForFloorPlanUpdateRefresh);
            immersive3dTourFloorPlanImportModalStore.load();
          }
        });
      }, 10000);
    }
  }

  requestFloorPlanUpdate() {
    const { immersive3dTourFloorPlanImportModalStore } = this.props;

    immersive3dTourFloorPlanImportModalStore
      .requestFloorPlanUpdate()
      .then(() => {
        this.setState({ hasRequestedFloorPlansUpdate: true });
        this.floorPlanUpdateAutoRefresh(true);
      })
      .catch(() => {
        // Do nothing
      });
  }

  renderTourJobFileType(mimeType) {
    switch (mimeType) {
      case "image/jpeg":
        return "JPG";
      case "image/png":
        return "PNG";
      case "image/svg":
      case "image/svg+xml":
        return "SVG";
      case "application/pdf":
      case "application/x-pdf":
        return "PDF";
      default:
        return "Other";
    }
  }

  render() {
    const {
      immersive3dTourFloorPlanImportModalStore,
      immersive3dTourStore,
      visible,
      hideModal,
      tourIsPocketWebsite,
    } = this.props;

    const {
      immersive3dTourFloorPlans,
      isLoading,
      newCheckedImmersive3dTourFloorPlans,
    } = immersive3dTourFloorPlanImportModalStore;

    const { immersive3dTour } = immersive3dTourStore;

    let importInTourButton = null;

    if (tourIsPocketWebsite || newCheckedImmersive3dTourFloorPlans.length === 0) {
      importInTourButton = (
        <Popup
          content={
            tourIsPocketWebsite
              ? "This feature is available only for Single Property Websites"
              : "Please select at least one floor plan"
          }
          trigger={<Button>Import in Tour</Button>}
        />
      );
    } else {
      importInTourButton = (
        <Button
          primary
          onClick={() => {
            immersive3dTourFloorPlanImportModalStore.importSelectedFloorPlans();
          }}
        >
          Import in Tour
        </Button>
      );
    }

    return (
      <Modal
        closeIcon
        className="floorPlanImportModal"
        open={visible}
        onClose={() => {
          hideModal();
        }}
      >
        <Modal.Header>Floor plans</Modal.Header>
        <Modal.Content>
          <Modal.Description>
            {isLoading && !this.hasPendingFloorPlanUpdateRequest && <ContentLoader />}
            {!isLoading && (
              <>
                {this.hasPendingFloorPlanUpdateRequest ? (
                  <Dimmer active inverted>
                    <Loader
                      inverted
                      content={
                        <div style={{ width: "250px" }}>
                          <p>Regenerating floor plans</p>
                          <p>This may take a few minutes ...</p>
                        </div>
                      }
                    />
                  </Dimmer>
                ) : (
                  <>
                    {immersive3dTourFloorPlans.length === 0 ? (
                      <Dimmer active inverted>
                        <Loader
                          inverted
                          content={<p style={{ width: "250px" }}>The floor plans are still in creation</p>}
                        />
                      </Dimmer>
                    ) : (
                      <>
                        <FlashMessage flashMessageStore={immersive3dTourFloorPlanImportModalStore.flashMessageStore} />
                        {immersive3dTour.floorPlanUpdateRequestDts !== null &&
                          immersive3dTour.floorPlanUpdateRequestDts > immersive3dTour.floorPlanLastUpdateDts &&
                          moment() >= immersive3dTour.floorPlanUpdateRequestTimeoutDts && (
                            <Message negative>
                              <p>Your request to regenerate the floor plans has timed out, please try again.</p>
                            </Message>
                          )}
                        <Card.Group itemsPerRow={4}>
                          {immersive3dTourFloorPlans.map(floorPlan => (
                            <Card key={floorPlan.id}>
                              <ImageWithLoading
                                src={floorPlan.fileUrl}
                                size="medium"
                                height="150"
                                onClickCallback={() =>
                                  immersive3dTourFloorPlanImportModalStore.toggleSelected(floorPlan.id)
                                }
                              />
                              <Card.Content>{floorPlan.name}</Card.Content>
                              <Card.Content extra>
                                {floorPlan.files.map(tourJobFile => (
                                  <form
                                    style={{ display: "inline-block" }}
                                    method="get"
                                    action={this.getTourJobFileDownloadUrl(tourJobFile.tourJobFileId)}
                                    key={tourJobFile.tourJobFileId}
                                  >
                                    <Button type="submit" size="mini" basic>
                                      <Icon name="cloud download" />
                                      {this.renderTourJobFileType(tourJobFile.mimeType)}
                                    </Button>
                                  </form>
                                ))}
                              </Card.Content>
                              {!tourIsPocketWebsite && (
                                <>
                                  {floorPlan.status === Immersive3dTourFloorPlanImportStatus.NEW ||
                                  (floorPlan.status === Immersive3dTourFloorPlanImportStatus.DONE &&
                                    this.allowImportForAlreadyImportedFloorPlans) ? (
                                    <Checkbox
                                      checked={floorPlan.checked}
                                      onChange={() =>
                                        immersive3dTourFloorPlanImportModalStore.toggleSelected(floorPlan.id)
                                      }
                                    />
                                  ) : null}
                                  {floorPlan.status === Immersive3dTourFloorPlanImportStatus.QUEUED ? (
                                    <Label>Pending</Label>
                                  ) : null}
                                  {floorPlan.status === Immersive3dTourFloorPlanImportStatus.DONE &&
                                  !this.allowImportForAlreadyImportedFloorPlans ? (
                                    <Label>Done</Label>
                                  ) : null}
                                </>
                              )}
                            </Card>
                          ))}
                        </Card.Group>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </Modal.Description>
        </Modal.Content>
        {!isLoading && !this.hasPendingFloorPlanUpdateRequest && (
          <Modal.Actions>
            <Button basic onClick={() => immersive3dTourFloorPlanImportModalStore.load()}>
              <Icon name="refresh" />
              Refresh
            </Button>
            {immersive3dTourFloorPlans.length > 0 && (
              <>
                <Button
                  disabled={this.hasPendingFloorPlanUpdateRequest}
                  secondary
                  onClick={() => this.requestFloorPlanUpdate()}
                >
                  Regenerate
                </Button>
                {importInTourButton}
              </>
            )}
          </Modal.Actions>
        )}
      </Modal>
    );
  }
}

FloorPlanImportModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  hideModal: PropTypes.func.isRequired,
  immersive3dTourFloorPlanImportModalStore: PropTypes.instanceOf(Immersive3dTourFloorPlanImportModalStore),
  immersive3dTourStore: PropTypes.instanceOf(Immersive3dTourStore),
  tourIsPocketWebsite: PropTypes.bool,
};

FloorPlanImportModal.defaultProps = {
  tourIsPocketWebsite: false,
};

export default FloorPlanImportModal;
